<script>
  // # # # # # # # # # # # # #
  //
  //  Slideshow
  //
  // # # # # # # # # # # # # #

  // *** IMPORT
  import { onMount } from "svelte";
  import { fly } from "svelte/transition";
  import Flickity from "flickity-imagesloaded";
  import get from "lodash/get";

  // *** COMPONENTS
  import FullscreenSlideshow from "./FullscreenSlideshow.svelte";
  import Video from "./Video.svelte";

  // *** PROPS
  export let slides = [];

  // *** DOM REFERENCES
  let slideShowEl = {};

  // *** VARIABLES
  let flkty = {};
  let fullScreen = false;
  let hovered = false;
  let startSlide = 0;

  // *** CONSTANTS
  const options = {
    wrapAround: true,
    prevNextButtons: false,
    pageDots: false,
    autoPlay: 10000,
    pauseAutoPlayOnHover: true,
    groupCells: 1,
    imagesLoaded: true,
    lazyLoad: 2,
    selectedAttraction: 0.028,
    friction: 0.28
  };

  const paddedSlides =
    get(slides, "length", 0) < 8 ? [...slides, ...slides] : slides;

  onMount(async () => {
    if (slideShowEl) {
      flkty = new Flickity(slideShowEl, options);
    }
  });
</script>

<style lang="scss">
  @import "../_variables.scss";

  .slideshow {
    $block: &;

    height: 100%;
    width: 50vw;

    @include screen-size("small") {
      height: 100vh;
      width: 100%;
      left: 0;
      position: static;
    }

    &__image {
      user-select: none;
      pointer-events: none;
    }

    &__overlay {
      height: 100%;
      width: 50vw;
      position: absolute;
      top: 0;
      left: 0;
      background: rgba(255, 255, 255, 0.6);
      opacity: 0;
      font-family: sans-serif;
      font-size: $font-size-large;
      font-weight: bold;
      display: flex;
      justify-content: center;
      align-items: center;
      transition: opacity $fast-easing;

      &:hover {
        opacity: 1;
      }

      @include screen-size("small") {
        height: 400px;
        width: 100%;
        padding-top: 100px;
      }
    }

    &__slide {
      width: 50vw;
      height: 100vh;
      display: flex;
      justify-content: center;
      align-items: center;
      outline: none;

      cursor: pointer;

      img {
        max-height: 100%;
      }

      iframe {
        max-width: 80%;
        max-height: 80%;
      }

      @include screen-size("small") {
        height: 400px;
        width: 100vh;
        padding-top: 100px;
      }

      &.full-width {
        #{ $block }__image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          @include screen-size("small") {
            object-fit: contain;
          }
        }

        @include screen-size("small") {
          object-fit: contain;
        }
      }

      &.proportional {
        #{ $block}__image {
          width: 90%;
          height: 90%;
          object-fit: contain;
        }
      }
    }

    &__caption {
      position: absolute;
      bottom: 80px;
      padding-left: 10%;
      padding-right: 10%;
      width: 100%;
      background: $white;
      padding: $main-margin;
      line-height: 1.3em;
      font-size: $font-size-small;
      font-family: $serif-stack;
      opacity: 1;
      text-align: center;
      background: white;
      user-select: none;

      @include screen-size("small") {
        display: none;
      }
    }
  }
</style>

<div class="carousel slideshow" bind:this={slideShowEl}>
  {#each paddedSlides as slide}
    <div
      class="carousel-cell slideshow__slide"
      class:full-width={slide.fullwidth == 1}
      class:proportional={slide.fullwidth == 0}>
      {#if slide.video}
        <Video url={slide.video} />
      {:else}
        <img
          class="slideshow__image"
          data-flickity-lazyload-srcset="{slide.sizes.small.url}
          {slide.sizes.small.width}w, {slide.sizes.medium.url}
          {slide.sizes.medium.width}w, {slide.sizes.large.url}
          {slide.sizes.large.width}w, {slide.sizes.xlarge.url}
          {slide.sizes.xlarge.width}w"
          sizes="40vw"
          data-flickity-lazyload-src={slide.sizes.medium.url}
          alt={slide.caption ? slide.caption : 'Complete Works'} />
      {/if}
    </div>
  {/each}
</div>

<div
  class="slideshow__overlay"
  on:mouseenter={() => {
    hovered = true;
  }}
  on:mouseleave={() => {
    hovered = false;
  }}
  on:click={e => {
    startSlide = flkty.selectedIndex;
    fullScreen = true;
  }}>

  {#if hovered}
    <div in:fly={{ y: 40, duration: 300 }} out:fly={{ y: 40, duration: 300 }}>
      Full screen slideshow
    </div>
  {/if}

</div>

<FullscreenSlideshow
  {slides}
  {startSlide}
  active={fullScreen}
  on:close={i => {
    flkty.selectCell(i, true, true);
    fullScreen = false;
  }} />
